import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, UserPlus, HandCoins, Trash2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { tk, bn, fmtDate } from "../lib/format";
import { Empty, Loader, Modal, Field, GlassPage, GlassHeader } from "../components/kit";
import ContactPicker from "../components/ContactPicker";
import { useDebounced } from "../components/ProductSearch";

function CustomerForm({ onDone }: { onDone: () => void }) {
  const [f, setF] = useState({ name: "", mobile: "", address: "", opening_due: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/customers", { ...f, opening_due: +f.opening_due || 0 });
      toast.success("ক্রেতা যোগ হয়েছে");
      onDone();
    } catch (er) {
      toast.error(errMsg(er));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="customer-form">
      <Field label="নাম *"><input className="inp" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="customer-name-input" autoFocus /></Field>
      <Field label="মোবাইল">
        <div className="flex gap-2">
          <input className="inp flex-1" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} data-testid="customer-mobile-input" />
          <ContactPicker testid="customer-contact-picker" onPick={(c) => setF((p) => ({ ...p, mobile: c.tel || p.mobile, name: p.name?.trim() ? p.name : (c.name || "") }))} />
        </div>
      </Field>
      <Field label="ঠিকানা"><input className="inp" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="customer-address-input" /></Field>
      <Field label="আগের বাকি (খাতা থেকে, যদি থাকে)"><input className="inp num" type="number" min="0" step="any" value={f.opening_due} onChange={(e) => setF({ ...f, opening_due: e.target.value })} data-testid="customer-opening-due-input" /></Field>
      <button className="btn btn-primary w-full" disabled={busy} data-testid="customer-form-submit">সেভ করুন</button>
    </form>
  );
}

function GStat({ label, value, tone, testid }: { label: string; value: string; tone: "red" | "slate"; testid: string }) {
  return (
    <div className="glass-card p-4 md:p-5" data-testid={testid}>
      <p className="text-sm font-medium text-slate-300">{label}</p>
      <p className={`num mt-2 text-xl md:text-2xl font-bold ${tone === "red" ? "text-rose-300" : "text-white"}`} data-testid={`${testid}-value`}>{value}</p>
    </div>
  );
}

export default function Customers() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [list, setList] = useState<any[] | null>(null);
  const [add, setAdd] = useState(false);
  const dq = useDebounced(q);
  const nav = useNavigate();

  const load = useCallback(() => {
    api.get("/customers", { params: { q: dq, status } }).then((r) => setList(r.data));
  }, [dq, status]);
  useEffect(() => { load(); }, [load]);

  const del = async (c: any) => {
    if (!window.confirm(`"${c.name}" ক্রেতাকে মুছে ফেলবেন?\nপরে আর্কাইভ থেকে রিস্টোর করা যাবে।`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success("ক্রেতা আর্কাইভে সরানো হয়েছে");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const totalDue = (list || []).reduce((s, c) => s + Math.max(0, c.due), 0);
  const dueCount = (list || []).filter((c) => c.due > 0).length;

  return (
    <GlassPage testid="customers-page">
      <GlassHeader
        title="বাকির খাতা"
        subtitle="বর্তমান বাকি = আগের বাকি + নতুন বিল − পরিশোধ"
        actions={<button className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white font-semibold transition-all active:scale-95 shadow-lg shadow-emerald-500/25" onClick={() => setAdd(true)} data-testid="customers-add-button"><UserPlus size={17} /> নতুন ক্রেতা</button>}
      />

      <div className="grid grid-cols-2 gap-3 mb-4">
        <GStat label="তালিকায় মোট বাকি" value={tk(totalDue)} tone="red" testid="ledger-total-due" />
        <GStat label="বাকি আছে এমন ক্রেতা" value={`${bn(dueCount)} জন`} tone="slate" testid="ledger-due-count" />
      </div>

      <div className="glass-card p-4 flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="ginp pl-10" placeholder="নাম / মোবাইল / ঠিকানা" value={q} onChange={(e) => setQ(e.target.value)} data-testid="customers-search-input" />
        </div>
        <select className="ginp sm:w-48" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="customers-status-filter">
          <option value="">সব ক্রেতা</option><option value="due">বাকি আছে</option><option value="clear">পরিশোধিত</option>
        </select>
      </div>

      <div className="glass-card overflow-x-auto">
        {!list ? <Loader /> : list.length === 0 ? <Empty text="কোনো ক্রেতা নেই" /> : (
          <table className="w-full text-sm" data-testid="customer-ledger-table">
            <thead>
              <tr className="text-slate-400 text-left border-b border-white/10">
                <th className="px-5 py-3 font-medium">ক্রেতা</th><th className="px-3 py-3 font-medium">মোবাইল</th>
                <th className="px-3 py-3 font-medium text-right">শুরুর বাকি</th><th className="px-3 py-3 font-medium text-right">মোট বিল</th>
                <th className="px-3 py-3 font-medium text-right">মোট পরিশোধ</th><th className="px-3 py-3 font-medium text-right">বর্তমান বাকি</th>
                <th className="px-3 py-3 font-medium">শেষ পেমেন্ট</th><th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`ledger-row-${c.id}`}>
                  <td className="px-5 py-3"><Link to={`/customers/${c.id}`} className="font-semibold text-blue-300 hover:underline">{c.name}</Link><span className="block text-xs text-slate-400">{c.address}</span></td>
                  <td className="px-3 py-3 num text-slate-300">{bn(c.mobile) || "—"}</td>
                  <td className="px-3 py-3 text-right num text-slate-300">{tk(c.opening_due)}</td>
                  <td className="px-3 py-3 text-right num text-slate-300">{tk(c.total_bill)}</td>
                  <td className="px-3 py-3 text-right num text-emerald-300">{tk(c.total_paid)}</td>
                  <td className={`px-3 py-3 text-right num font-bold ${c.due > 0 ? "text-rose-300" : "text-emerald-300"}`} data-testid={`ledger-due-${c.id}`}>{tk(c.due)}</td>
                  <td className="px-3 py-3 num text-slate-300">{fmtDate(c.last_payment_date)}</td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    {c.due > 0 && <button className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold transition-all active:scale-95" onClick={() => nav(`/collection?customer=${c.id}`)} data-testid={`customer-payment-receive-button-${c.id}`}><HandCoins size={15} /> আদায়</button>}
                    <button className="w-9 h-9 ml-1 inline-grid place-items-center rounded-lg text-rose-400 hover:bg-rose-500/10 transition-all active:scale-95" onClick={() => del(c)} data-testid={`customer-delete-${c.id}`} aria-label="মুছুন"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={add} onClose={() => setAdd(false)} title="নতুন ক্রেতা" testid="customer-add-modal">
        <CustomerForm onDone={() => { setAdd(false); load(); }} />
      </Modal>
    </GlassPage>
  );
}
